import { onBeforeUnmount } from 'vue';

import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import { MemberType } from './enum';
import { createTypingMessage, findMessageIndex } from './lib';
import type { MemberKind, MessageListItem } from './types';
import { useMessages, type MessageApi } from './useMessages';

export interface MessageUtilities extends MessageApi {
  /**
   * Enqueue a promise message/messages. This will create a typing message and then replace that
   * typing message with the resulting message. This will quickly let you enqueue loading messages
   * which will be replaced with the the messages returned from the promises.
   *
   * @author Aakash More <moreakash77@hotmail.com>
   */
  enqueue<T extends MessageListItem>(
    factory: Promise<T[]> | (() => Promise<T[]>),
    options?: EnqueueOptions
  ): Promise<T[] | null>;
  enqueue<T extends MessageListItem>(
    factory: Promise<T> | (() => Promise<T>),
    options?: EnqueueOptions
  ): Promise<T | null>;
  enqueue<T extends MessageListItem | null>(
    factory: Promise<T> | (() => Promise<T>),
    options?: EnqueueOptions
  ): Promise<T | null>;

  /**
   * Listen to the events of the MessagesApi. This will automatically clear
   * the event in `onBeforeUnmount` lifecycle phase of the component. This will
   * also return a `removeListener` method that allows to unsubscribe form
   * listening the events.
   *
   * @author Aakash More <moreakash77@hotmail.com>
   */
  listen(...args: Parameters<MessageApi['events']['on']>): () => void;
}

interface EnqueueOptions {
  /**
   * Loading user type.
   */
  memberKind?: MemberKind;
  /**
   * If enabled, then this will rethrow any error occurred while loading the
   * message from factory. You can then handle that error via `promise.catch(err)`.
   */
  enableError?: boolean;
  /**
   * This will allow cancel the action performed after the API is completed.
   */
  ignoreSignal?: () => boolean;
}

/**
 * Add helper functions to the an existing/new MessagesApi to perform certain actions
 * like `enqueue` promise based messages etc.
 * @param factory The MessagesApi. If not specified, a new API is created.
 * @returns API with enhanced helpers.
 */
export function useMessageUtilities(factory?: MessageApi): MessageUtilities {
  const api = factory || useMessages();

  const { addItems, deleteItem, addItemsAt, messages, events } = api;

  /**
   * Enqueue a promise message/messages. This will create a typing message and then replace that
   * typing message with the resulting from the promise. This will quickly let you enqueue loading and
   * message from APIs.
   */
  async function enqueue<T extends MessageListItem>(
    futureMessage: Promise<T | T[]> | (() => Promise<T | T[]>),
    { memberKind = MemberType.AGENT, enableError = false, ignoreSignal = () => false }: EnqueueOptions = {}
  ): Promise<T | T[] | null> {
    const typingMessage = createTypingMessage(null, memberKind || MemberType.AGENT);
    addItems(typingMessage);

    const IGNORE_MESSAGE = Symbol();
    const ignoreIfSignaled = (r: T | T[]) => (ignoreSignal?.() ? Promise.reject(IGNORE_MESSAGE) : r);

    try {
      const messageOrList = await (typeof futureMessage === 'function' ? futureMessage() : futureMessage).then(
        ignoreIfSignaled
      );
      const messageQueue = Array.isArray(messageOrList) ? messageOrList : [messageOrList];
      const filteredMessageQueue = messageQueue.filter((message) => !!message);
      const idx = findMessageIndex(messages.value, typingMessage);
      deleteItem(typingMessage);
      addItemsAt(idx >= 0 ? idx : null, ...filteredMessageQueue);
      return messageOrList;
    } catch (err) {
      deleteItem(typingMessage);
      if (err === IGNORE_MESSAGE) return null;
      // Callers do not set `enableError`, so the typing bubble simply disappears with no message
      // replacing it — without a report the whole class of queued flow failures stays invisible.
      captureEventException(err, ErrorTrackingEvent.ChatbotMessageQueue);
      if (enableError) throw err;
    }
    return null;
  }

  /**
   * Listen to the events of the MessagesApi. This will automatically clear
   * the event in `onBeforeUnmount` lifecycle phase of the component. This will
   * also return a `removeListener` method that allows to unsubscribe form
   * listening the events.
   *
   * @author Aakash More <moreakash77@hotmail.com>
   */
  function listen(...args: Parameters<MessageApi['events']['on']>) {
    const [type, handler] = args;
    events.on(type, handler);
    const removeListener = () => events.off(type, handler);
    onBeforeUnmount(removeListener);
    return removeListener;
  }

  return {
    ...api,
    enqueue,
    listen,
  };
}
